import { useRef } from 'react';
import ProfileAvatar from './ProfileAvatar';
import EditIconButton from './EditIconButton';
import { useChangeProfileMutation } from '../hooks/useChangeProfileMutation';
import type { UserProfileType } from '../../types/UserType';

type ProfileAvatarSectionProps = {
  thumbnailUrl?: UserProfileType['thumbnailUrl'];
  userProfileId?: UserProfileType['userProfileId'];
  nickname?: UserProfileType['nickname'];
  description?: UserProfileType['description'];
  onCreateProfile?: () => void;
};

const ProfileAvatarSection = ({
  thumbnailUrl,
  userProfileId,
  nickname,
  description,
  onCreateProfile
}: ProfileAvatarSectionProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { changeProfileMutation, isLoading } = useChangeProfileMutation();

  const handleEditClick = () => {
    if (!userProfileId && onCreateProfile) {
      // 프로필이 없으면 프로필 생성 콜백 실행
      onCreateProfile();
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('이미지 파일만 선택 가능합니다.');
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      alert('파일 크기는 5MB 이하여야 합니다.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;

      changeProfileMutation(
        {
          userProfileId,
          nickname,
          description: description || '',
          dataUrl
        },
        {
          onError: (_error) => {
            alert('이미지 업데이트에 실패했습니다. 다시 시도해주세요.');
          }
        }
      );
    };

    reader.readAsDataURL(file);

    event.target.value = '';
  };

  return (
    <div role="none" className="relative" onClick={handleEditClick}>
      <ProfileAvatar
        widthAndHeight={'56px'}
        svgWidthAndHeight={'40px'}
        padding={'8px'}
        thumbnailUrl={thumbnailUrl}
      />
      <EditIconButton
        className="absolute bottom-0 right-0 border-[2px] border-pure-white dark:border-backgroundElevated-dark cursor-pointer"
        disabled={isLoading}
      />

      {/* 프로필이 있을 때만 파일 input 렌더링 */}
      {userProfileId && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
          disabled={isLoading}
        />
      )}
    </div>
  );
};

export default ProfileAvatarSection;
